import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import PopupForm from './components/PopupForm';
import { Toaster } from './components/ui/sonner';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LandingPage from './pages/LandingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ApplicationPage from './pages/ApplicationPage';

// Service Pages
import RegisterTrademark from './pages/services/RegisterTrademark';
import ServicePage from './pages/services/ServicePage';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AppContent isPopupOpen={isPopupOpen} setIsPopupOpen={setIsPopupOpen} />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </HelmetProvider>
  );
}

function AppContent({ isPopupOpen, setIsPopupOpen }) {
  const location = useLocation();
  const isLandingPage = location.pathname === '/lp';

  return (
    <>
      <Header onOpenPopup={() => setIsPopupOpen(true)} isLandingPage={isLandingPage} />
      <Routes>
        <Route path="/" element={<HomePage onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/lp" element={<LandingPage onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/application/main" element={<ApplicationPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        
        {/* Trademark Services */}
        <Route path="/services/register-trademark" element={<RegisterTrademark onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/ttab-petition" element={<ServicePage serviceName="TTAB Petition" />} />
        <Route path="/services/clearance-search" element={<ServicePage serviceName="Clearance Search Services" />} />
        <Route path="/services/ownership-transfers" element={<ServicePage serviceName="Trademark Ownership Transfers" />} />
        <Route path="/services/trademark-renewal" element={<ServicePage serviceName="Managed Trademark Renewal" />} />
        <Route path="/services/statements-of-use" element={<ServicePage serviceName="Submission of Statements of Use" />} />
        <Route path="/services/office-actions" element={<ServicePage serviceName="Support & Response for Trademark Office Actions" />} />
        <Route path="/services/infringement-monitoring" element={<ServicePage serviceName="Active Monitoring for Trademark Infringement" />} />
        <Route path="/services/trademark-cancellations" element={<ServicePage serviceName="Support & Guidance for Trademark Cancellations" />} />
        <Route path="/services/trademark-disputes" element={<ServicePage serviceName="Legal Representation for Trademark Disputes" />} />
        <Route path="/services/counterfeiting" element={<ServicePage serviceName="Combatting Counterfeiting & Trademark Infringement" />} />
        
        {/* Copyright Services */}
        <Route path="/services/register-copyright" element={<ServicePage serviceName="Register Your Copyright" />} />
        <Route path="/services/copyright-enforcement" element={<ServicePage serviceName="Copyright Enforcement Assistance" />} />
        <Route path="/services/copyright-transfers" element={<ServicePage serviceName="Copyright Transfers & Licensing Support" />} />
        <Route path="/services/copyright-legal" element={<ServicePage serviceName="Legal Representation for Copyright" />} />
        <Route path="/services/copyright-portfolio" element={<ServicePage serviceName="Copyright Portfolio Management" />} />
        <Route path="/services/dmca-compliance" element={<ServicePage serviceName="DMCA Compliance" />} />
        
        {/* Patent Services */}
        <Route path="/services/patent-search" element={<ServicePage serviceName="Existing Patent Search" />} />
        <Route path="/services/patent-office-actions" element={<ServicePage serviceName="Response to Patent Office Actions" />} />
        <Route path="/services/patent-application" element={<ServicePage serviceName="Patent Application Submission" />} />
        <Route path="/services/patent-protection" element={<ServicePage serviceName="Legal Representation for Patent Protection" />} />
        
        {/* Company Formation */}
        <Route path="/services/llc-formation" element={<ServicePage serviceName="LLC Formation" />} />
        <Route path="/services/c-corporation" element={<ServicePage serviceName="C Corporation Formation" />} />
        <Route path="/services/s-corporation" element={<ServicePage serviceName="S Corporation Formation" />} />
        <Route path="/services/non-profit" element={<ServicePage serviceName="Non-Profit Formation" />} />
        
        {/* Legal Services */}
        <Route path="/services/partnerships" element={<ServicePage serviceName="Legal Consultation on Partnerships & LLPs" />} />
        <Route path="/services/due-diligence" element={<ServicePage serviceName="Due Diligence" />} />
        <Route path="/services/immigration-law" element={<ServicePage serviceName="Business Immigration Law" />} />
        <Route path="/services/commercial-law" element={<ServicePage serviceName="IT & Commercial Law" />} />
        <Route path="/services/data-protection" element={<ServicePage serviceName="Data Protection & Privacy Law" />} />
        
        {/* Additional Services */}
        <Route path="/services/amazon-brand-registry" element={<ServicePage serviceName="Amazon Brand Registry (US)" />} />
        <Route path="/services/business-formation" element={<ServicePage serviceName="Business Formation Services" />} />
        <Route path="/services/company-dissolution" element={<ServicePage serviceName="Company Dissolution" />} />
        <Route path="/services/issue-shares" element={<ServicePage serviceName="Issue of Shares" />} />
        <Route path="/services/transfer-shares" element={<ServicePage serviceName="Transfer of Shares" />} />
        <Route path="/services/good-standing" element={<ServicePage serviceName="Certificate of Good Standing" />} />
        <Route path="/services/name-change" element={<ServicePage serviceName="Company Name Change" />} />
        <Route path="/services/company-renewals" element={<ServicePage serviceName="Company Renewals" />} />
        <Route path="/services/nominee-director" element={<ServicePage serviceName="Nominee Director & Secretarial Services" />} />
        <Route path="/services/bookkeeping" element={<ServicePage serviceName="Bookkeeping" />} />
        <Route path="/services/tax-advisory" element={<ServicePage serviceName="Tax Advisory & Filing" />} />
        <Route path="/services/cfo-services" element={<ServicePage serviceName="CFO Services" />} />
      </Routes>
      {!isLandingPage && <Footer />}
      <PopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
}

export default App;